import React, { useState, useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import config from '../../config';
import './SellerDetailsCard.css';
import axios from 'axios';
import { useLanguage } from '../../i18n/LanguageContext';
import { propertyTypeOptions, translatePropertyType } from '../../i18n/translations';
import { getMapboxCountry, getMapboxLanguage } from '../../utils/mapboxCountry';

mapboxgl.accessToken = config.mapbox_token;

function SellerDetailsCard({ data, onUpdate, readOnly = false }) {
  const {
    id,
    address,
    email,
    display_contact_info,
    latitude,
    longitude,
    seller_id
  } = data;

  // State variables for UI feedback
  const [isEditMode, setIsEditMode] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [geocodedCoordinates, setGeocodedCoordinates] = useState(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editableLatitude, setEditableLatitude] = useState(latitude || null);
  const [editableLongitude, setEditableLongitude] = useState(longitude || null);
  const { t, language } = useLanguage();

  // Editable fields
  const [editableAddress, setEditableAddress] = useState(address || '');
  const [editableArea, setEditableArea] = useState(data.area || '');
  const [editableNumberOfRooms, setEditableNumberOfRooms] = useState(data.number_of_rooms || '');
  const [editablePropertyType, setEditablePropertyType] = useState(data.property_type || '');
  const [editableDisplayContactInfo, setEditableDisplayContactInfo] = useState(display_contact_info || false);

  const mapContainer = useRef(null);
  const map = useRef(null);
  
  // Add state for address suggestions
  const [addressSuggestions, setAddressSuggestions] = useState([]);

  // Add validation errors state
  const [validationErrors, setValidationErrors] = useState({});


  
  // State for län
  const [editableLan, setEditableLan] = useState(data.lan || '');
  
  // Check if current user is the seller using simpler approach
  const [isSeller, setIsSeller] = useState(false);



  // Validation functions

  const validateAddress = (address) => {
    if (!address) {
      return t('objectForm.validation.addressRequired');
    }
    if (address.length < 5) {
      return t('objectForm.validation.addressLength');
    }
    return '';
  };

  const validateArea = (area) => {
    const areaStr = String(area || '');
    if (!area || areaStr.trim() === '') {
      return t('objectForm.validation.areaRequired');
    }
    const numArea = parseInt(areaStr, 10);
    if (isNaN(numArea) || numArea <= 0) {
      return t('valuationChat.invalidNumber');
    }
    return '';
  };

  const validateNumberOfRooms = (rooms) => {
    const roomsStr = String(rooms || '');
    if (!rooms || roomsStr.trim() === '') {
      return t('objectForm.validation.roomsRequired');
    }
    const numRooms = parseInt(roomsStr, 10);
    if (isNaN(numRooms) || numRooms <= 0) {
      return t('valuationChat.invalidNumber');
    }
    return '';
  };

  const validatePropertyType = (propertyType) => {
    const propertyTypeStr = String(propertyType || '');
    if (!propertyType || propertyTypeStr.trim() === '') {
      return t('objectForm.validation.propertyRequired');
    }
    return '';
  };

  // Function to validate a single field
  const validateField = (fieldName, value) => {
    let error = '';
    
    switch (fieldName) {
      case 'address':
        error = validateAddress(value);
        break;
      case 'area':
        error = validateArea(value);
        break;
      case 'number_of_rooms':
        error = validateNumberOfRooms(value);
        break;
      case 'property_type':
        error = validatePropertyType(value);
        break;
      default:
        break;
    }

    setValidationErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));

    return error === '';
  };

  // Function to validate all fields
  const validateAllFields = () => {
    const errors = {};
    
    errors.address = validateAddress(editableAddress);
    errors.area = validateArea(editableArea);
    errors.number_of_rooms = validateNumberOfRooms(editableNumberOfRooms);
    errors.property_type = validatePropertyType(editablePropertyType);
    
    // Remove empty errors
    Object.keys(errors).forEach(key => {
      if (!errors[key]) {
        delete errors[key];
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Component to display validation errors
  const ValidationErrorMessage = ({ fieldName }) => {
    const error = validationErrors[fieldName];
    if (!error) return null;
    
    return (
      <div className="validation-error">
        {error}
      </div>
    );
  };

  // Enhanced input change handlers with validation

  const handleAddressBlur = (e) => {
    const value = e.target.value;
    validateField('address', value);
  };

  const handleAreaChange = (e) => {
    const value = e.target.value;
    setEditableArea(value);
  };

  const handleAreaBlur = (e) => {
    const value = e.target.value;
    validateField('area', value);
  };

  const handleNumberOfRoomsChange = (e) => {
    const value = e.target.value;
    setEditableNumberOfRooms(value);
  };

  const handleNumberOfRoomsBlur = (e) => {
    const value = e.target.value;
    validateField('number_of_rooms', value);
  };

  const handlePropertyTypeChange = (e) => {
    const value = e.target.value;
    setEditablePropertyType(value);
  };

  const handlePropertyTypeBlur = (e) => {
    const value = e.target.value;
    validateField('property_type', value);
  };

  // Handle address change with autocomplete
  const handleAddressChange = async (e) => {
    const value = e.target.value;
    setEditableAddress(value);

    // Only search if the value is sufficiently long
    if (value.length > 3) {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            value
          )}.json?access_token=${mapboxgl.accessToken}&autocomplete=true&limit=5&country=${getMapboxCountry(language)}&language=${getMapboxLanguage(language)}`
        );
        const data = await response.json();
        setAddressSuggestions(data.features);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

  // When a suggestion is clicked, update address and geolocation
  const handleSuggestionClick = async (feature) => {
    setEditableAddress(feature.place_name);
    // Mapbox returns [lng, lat]
    const lng = feature.center[0];
    const lat = feature.center[1];
    
    setGeocodedCoordinates({ 
      longitude: lng, 
      latitude: lat 
    });
    
    // Also update the editable coordinates
    setEditableLatitude(lat);
    setEditableLongitude(lng);
    
    // Extract län from Mapbox context - look for 'region' place type
    let extractedLan = '';
    if (feature.context) {
      // Look for region context
      for (const context of feature.context) {
        if (context.id && context.id.startsWith('region')) {
          extractedLan = context.text || '';
          // Remove "län" suffix if present and standardize (same as populate_lan_field.py)
          extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
          break;
        }
      }
    }
    
    // Also check if the feature itself is a region (same logic as populate_lan_field.py)
    if (!extractedLan && feature.place_type && feature.place_type.includes('region')) {
      extractedLan = feature.text || '';
      extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
    }
    
    // If no län found, try reverse geocoding with coordinates (same as Python script)
    if (!extractedLan) {
      try {
        const reverseResponse = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}&country=${getMapboxCountry(language)}&types=region&language=${getMapboxLanguage(language)}`
        );
        
        if (reverseResponse.ok) {
          const reverseData = await reverseResponse.json();
          if (reverseData.features && reverseData.features.length > 0) {
            for (const reverseFeature of reverseData.features) {
              if (reverseFeature.place_type && reverseFeature.place_type.includes('region')) {
                extractedLan = reverseFeature.text || '';
                extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
                break;
              }
            }
          }
        }
      } catch (reverseError) {
        console.warn('Reverse geocoding failed:', reverseError);
      }
    }
    
    setEditableLan(extractedLan);
    setAddressSuggestions([]);
    validateField('address', feature.place_name);
  };

  // Effect to geocode the address if no coordinates are provided
  useEffect(() => {
    async function geocodeAddress() {
      if ((!editableLatitude || !editableLongitude) && editableAddress && !isGeocoding && !geocodedCoordinates) {
        setIsGeocoding(true);

        try {
          // Use Mapbox Geocoding API to convert address to coordinates
          const encodedAddress = encodeURIComponent(editableAddress);
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${mapboxgl.accessToken}&country=${getMapboxCountry(language)}&language=${getMapboxLanguage(language)}`
          );

          if (!response.ok) {
            throw new Error(`Geocoding failed: ${response.statusText}`);
          }

          const data = await response.json();

          if (data.features && data.features.length > 0) {
            const [lng, lat] = data.features[0].center;
            setGeocodedCoordinates({ latitude: lat, longitude: lng });
            
            // Extract län from geocoding result
            const feature = data.features[0];
            let extractedLan = '';
            if (feature.context) {
              // Look for region context
              for (const context of feature.context) {
                if (context.id && context.id.startsWith('region')) {
                  extractedLan = context.text || '';
                  // Remove "län" suffix if present and standardize (same as populate_lan_field.py)
                  extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
                  break;
                }
              }
            }
            
            // Also check if the feature itself is a region (same logic as populate_lan_field.py)
            if (!extractedLan && feature.place_type && feature.place_type.includes('region')) {
              extractedLan = feature.text || '';
              extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
            }
            
            // If no län found, try reverse geocoding with coordinates (same as Python script)
            if (!extractedLan) {
              try {
                const reverseResponse = await fetch(
                  `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}&country=${getMapboxCountry(language)}&types=region&language=${getMapboxLanguage(language)}`
                );
                
                if (reverseResponse.ok) {
                  const reverseData = await reverseResponse.json();
                  if (reverseData.features && reverseData.features.length > 0) {
                    for (const reverseFeature of reverseData.features) {
                      if (reverseFeature.place_type && reverseFeature.place_type.includes('region')) {
                        extractedLan = reverseFeature.text || '';
                        extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
                        break;
                      }
                    }
                  }
                }
              } catch (reverseError) {
                console.warn('Reverse geocoding failed in useEffect:', reverseError);
              }
            }
            
            setEditableLan(extractedLan);
          } else {
            console.error("No geocoding results found for address:", editableAddress);
            setMapError(t('home.addressSuggestionRequired'));
          }
        } catch (error) {
          console.error("Geocoding error:", error);
          setMapError(`${t('home.addressSuggestionRequired')}: ${error.message}`);
        } finally {
          setIsGeocoding(false);
        }
      }
    }

    geocodeAddress();
  }, [editableAddress, editableLatitude, editableLongitude, geocodedCoordinates, isGeocoding]);

  // Use either provided coordinates or geocoded ones
  const effectiveLatitude = editableLatitude || (geocodedCoordinates && geocodedCoordinates.latitude);
  const effectiveLongitude = editableLongitude || (geocodedCoordinates && geocodedCoordinates.longitude);

  useEffect(() => {
    if (!isEditMode && effectiveLatitude && effectiveLongitude && mapContainer.current) {

      // Always clean up existing map first
      if (map.current) {
        map.current.remove();
        map.current = null;
      }

      // Add a delay to ensure container is ready and previous map is fully cleaned up
      const initializeMap = () => {
        // Double-check container is still available
        if (!mapContainer.current) {
          return;
        }

        try {
          // Initialize the map
          map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/streets-v11',
            center: [parseFloat(effectiveLongitude), parseFloat(effectiveLatitude)],
            zoom: 14,
          });

          // Add navigation controls
          map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

          // For privacy purposes, adjust marker position when display_contact_info is false,
          // and user type is not seller (i.e., broker or simulateBrokerView)
          const userType = localStorage.getItem('userType');
          const shouldApplyPrivacyOffset = !display_contact_info && (userType === 'broker' || 
              (userType === 'seller' && window.location.search.includes('view=broker')));
          
          let markerPosition = [parseFloat(effectiveLongitude), parseFloat(effectiveLatitude)];
          
          // If privacy is enabled, offset the marker to the nearest street
          // This is a simple offset to simulate privacy - in a real app you might want to use
          // an API to snap to the nearest street
          if (shouldApplyPrivacyOffset) {
            // Add small random offset to latitude and longitude (approximately 50-100 meters)
            const latOffset = (Math.random() * 0.001) - 0.0005; // ~50-100m offset
            const lngOffset = (Math.random() * 0.001) - 0.0005; // ~50-100m offset
            markerPosition = [
              parseFloat(effectiveLongitude) + lngOffset,
              parseFloat(effectiveLatitude) + latOffset
            ];
          }

          // Add marker at possibly adjusted position
          new mapboxgl.Marker()
            .setLngLat(markerPosition)
            .addTo(map.current);

          // Add event listeners to debug map loading issues
          map.current.on('error', (e) => {
            console.error("Map error event:", e);
            setMapError(`Map error: ${e.error?.message || 'Unknown error'}`);
          });

          // Force map to resize and render correctly
          map.current.on('load', () => {
            if (map.current) {
              map.current.resize();
            }
          });

          // Also resize after a short delay to ensure proper rendering
          setTimeout(() => {
            if (map.current) {
              map.current.resize();
            }
          }, 100);
        } catch (error) {
          console.error("Error initializing map:", error);
          console.error("Error details:", {
            name: error.name,
            message: error.message,
            stack: error.stack
          });
          setMapError(`${t('home.addressSuggestionRequired')}: ${error.message}`);
        }
      };

      // Longer delay to ensure proper cleanup and container readiness
      setTimeout(initializeMap, 100);
    }
  }, [isEditMode, effectiveLatitude, effectiveLongitude, display_contact_info]);

  // Effect to handle resizing the map when it becomes visible
  useEffect(() => {
    if (!isEditMode && map.current) {
      map.current.resize();
    }
  }, [isEditMode]);

  // Cleanup effect to remove map when component unmounts
  useEffect(() => {
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);





  // Check if current user is the seller using simpler and more reliable approach
  useEffect(() => {
    const userType = localStorage.getItem('userType');
    // For sellers, show edit button if they're viewing their own data
    // This is a simplified approach - you may want to add more specific user ID checking later
    const isSellerUser = userType === 'seller';
    setIsSeller(isSellerUser && !readOnly);
  }, [readOnly]);



  // Reset fields when component refreshes
  useEffect(() => {
    setEditableAddress(address || '');
    setEditableArea(data.area || '');
    setEditableNumberOfRooms(data.number_of_rooms || '');
    setEditablePropertyType(data.property_type || '');
    setEditableDisplayContactInfo(display_contact_info || false);
    setEditableLatitude(latitude);
    setEditableLongitude(longitude);
    setEditableLan(data.lan || '');
  }, [address, data.area, data.number_of_rooms, data.property_type, display_contact_info, latitude, longitude, data.lan]);

  const handleEditButtonClick = () => {
    // Force map cleanup before changing edit mode
    if (map.current) {
      map.current.remove();
      map.current = null;
    }
    
    setIsEditMode(!isEditMode);
    // Clear validation errors when entering/exiting edit mode
    setValidationErrors({});
  };

  // Separate cancel function to handle field reset and map cleanup
  const handleCancelButtonClick = () => {
    // Reset all editable fields to original values
    setEditableAddress(address || '');
    setEditableArea(data.area || '');
    setEditableNumberOfRooms(data.number_of_rooms || '');
    setEditablePropertyType(data.property_type || '');
    setEditableDisplayContactInfo(display_contact_info || false);
    setEditableLatitude(latitude || null);
    setEditableLongitude(longitude || null);
    setEditableLan(data.lan || '');
    
    // Clear suggestions and validation errors
    setAddressSuggestions([]);
    setValidationErrors({});
    
    // Force map cleanup
    if (map.current) {
      map.current.remove();
      map.current = null;
    }
    
    // Exit edit mode
    setIsEditMode(false);
  };

  // Function to save the simplified data
  const handleSaveButtonClick = async () => {
    setIsSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    // Validate all fields before submission
    if (!validateAllFields()) {
      setIsSaving(false);
      return;
    }

    try {
      // Create simplified update object with only essential fields for SellerData
      // Remove phone from here since it's now managed in Seller model
      const updatedData = {
        id: data.id,
        address: editableAddress,
        area: editableArea ? Number(editableArea) : null,
        number_of_rooms: editableNumberOfRooms ? Number(editableNumberOfRooms) : null,
        property_type: editablePropertyType,
        display_contact_info: Boolean(editableDisplayContactInfo),
        latitude: editableLatitude ? Number(editableLatitude) : null,
        longitude: editableLongitude ? Number(editableLongitude) : null,
        lan: editableLan || '',
      };

      // Update seller data
      await onUpdate(updatedData);
      
      // Force map re-initialization when exiting edit mode
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      
      setIsEditMode(false);
    } catch (error) {
      console.error('Error updating seller data:', error);
      setErrorMessage(`${t('objectForm.validation.unexpected')} ${error.message || ''}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`seller-details-card ${readOnly ? 'seller-details-card--readonly' : ''}`}>
      {errorMessage && <p className="error-message">{errorMessage}</p>}
      {successMessage && <p className="success-message">{successMessage}</p>}
      
      {readOnly ? (
        // In readOnly mode, only render the map
        <>
          {/* Display Mapbox map if geolocation is available or being fetched */}
          {(effectiveLatitude && effectiveLongitude) ? (
            <div
              ref={mapContainer}
              className="map-container"
            />
          ) : isGeocoding ? (
            <div className="map-loading">
              <p>{t('valuationResult.analyzing')}</p>
            </div>
          ) : (
            <div className="map-loading">
              <p>{t('objectDashboard.noImage')}</p>
            </div>
          )}
          {mapError && <p className="map-error">{mapError}</p>}
        </>
      ) : isEditMode ? (
        <>
          <h3 className="seller-details-card__title">{t('brokerDashboard.edit')}</h3>

          {/* Basic information section */}
          <h4 className="seller-details-card__section-title seller-details-card__section-title--first">{t('printLead.propertyInfo')}</h4>
          <label>{t('common.address')}:</label>
          {/* Only show validation error for address if there are no suggestions */}
          {validationErrors.address && addressSuggestions.length === 0 && (
            <div className="validation-error">
              {validationErrors.address}
            </div>
          )}

          <input
            type="text"
            value={editableAddress}
            onChange={handleAddressChange}
            onBlur={handleAddressBlur}
            placeholder={t('common.address')}
            className={`seller-details-card__input ${validationErrors.address ? 'input-error' : ''}`}
          />
          
          {/* Show address suggestions */}
          {addressSuggestions.length > 0 && (
            <ul className="address-suggestions">
              {addressSuggestions.map((feature) => (
                <li
                  key={feature.id}
                  onClick={() => handleSuggestionClick(feature)}
                  className="address-suggestion-item"
                  onMouseOver={(e) => e.target.classList.add('address-suggestion-item--hover')}
                  onMouseOut={(e) => e.target.classList.remove('address-suggestion-item--hover')}
                >
                  {feature.place_name}
                </li>
              ))}
            </ul>
          )}

          <label>{t('objectForm.area')}:</label>
          <input
            type="number"
            value={editableArea}
            onChange={handleAreaChange}
            onBlur={handleAreaBlur}
            placeholder={t('valuationChat.placeholders.area')}
            className={`seller-details-card__input ${validationErrors.area ? 'input-error' : ''}`}
            min="1"
          />
          <ValidationErrorMessage fieldName="area" />

          <label>{t('objectForm.rooms')}:</label>
          <input
            type="number"
            value={editableNumberOfRooms}
            onChange={handleNumberOfRoomsChange}
            onBlur={handleNumberOfRoomsBlur}
            placeholder={t('valuationChat.placeholders.rooms')}
            className={`seller-details-card__input ${validationErrors.number_of_rooms ? 'input-error' : ''}`}
            min="1"
          />
          <ValidationErrorMessage fieldName="number_of_rooms" />

          <label>{t('objectForm.propertyType')}:</label>
          <select
            value={editablePropertyType}
            onChange={handlePropertyTypeChange}
            onBlur={handlePropertyTypeBlur}
            className={`seller-details-card__input ${validationErrors.property_type ? 'input-error' : ''}`}
          >
            <option value="">{t('valuationChat.placeholders.propertyType')}</option>
            {propertyTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {translatePropertyType(option.value, language)}
              </option>
            ))}
          </select>
          <ValidationErrorMessage fieldName="property_type" />



          <div className="seller-details-card__checkbox-container">
            <label htmlFor="displayContactInfo" className="seller-details-card__checkbox-label">
              {t('objectForm.displayContact')}
            </label>
            &nbsp;        
            &nbsp;
            &nbsp;
            &nbsp;

            <input
              type="checkbox"
              id="displayContactInfo"
              checked={editableDisplayContactInfo}
              onChange={(e) => setEditableDisplayContactInfo(e.target.checked)}
              className="seller-details-card__checkbox"
            />
          </div>

          <div className="seller-details-card__button-container">
            <button 
              onClick={handleCancelButtonClick}
              className="seller-details-card__button seller-details-card__button--cancel"
              disabled={isSaving}
            >
              {t('common.cancel')}
            </button>
            <button 
              onClick={handleSaveButtonClick} 
              className="seller-details-card__button seller-details-card__button--save"
              disabled={isSaving}
            >
              {isSaving ? t('sellerLanding.saving') : t('brokerDashboard.save')}
            </button>
          </div>
        </>
      ) : (
        <>
          <h3 className="seller-details-card__title">Information</h3>

          {/* Basic information section */}
          <h4 className="seller-details-card__section-title seller-details-card__section-title--first">{t('printLead.propertyInfo')}</h4>
          <p><strong>{t('common.address')}:</strong> {address}</p>
          <p><strong>{t('objectForm.area')}:</strong> {data.area ? `${data.area} m²` : t('common.notProvided')}</p>
          <p><strong>{t('objectForm.rooms')}:</strong> {data.number_of_rooms || t('common.notProvided')}</p>
          <p><strong>{t('objectForm.propertyType')}:</strong> {data.property_type ? translatePropertyType(data.property_type, language) : t('common.notProvided')}</p>

          {/* Contact information section */}
          <h4 className="seller-details-card__section-title">{t('sellerLanding.contactInfo')}</h4>
          <p>
            <strong>{t('objectForm.displayContact')}</strong>{' '}
            {display_contact_info ? (
              <span className="status-visible">{t('common.yes')}</span>
            ) : (
              <span className="status-hidden">{t('common.no')}</span>
            )}
          </p>

          {/* Display Mapbox map if geolocation is available or being fetched */}
          <h4 className="seller-details-card__section-title">{t('objectDashboard.mapLink')}</h4>
          {(effectiveLatitude && effectiveLongitude) ? (
            <div
              ref={mapContainer}
              className="map-container"
            />
          ) : isGeocoding ? (
            <div className="map-loading">
              <p>{t('valuationResult.analyzing')}</p>
            </div>
          ) : (
            <div className="map-loading">
              <p>{t('objectDashboard.noImage')}</p>
            </div>
          )}
          {mapError && <p className="map-error">{mapError}</p>}

          {isSeller && (
            <button 
              onClick={handleEditButtonClick} 
              className="seller-details-card__button seller-details-card__button--edit"
            >
              {t('smallWidgets.edit')}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default SellerDetailsCard;
